import { createSlice, type PayloadAction, createSelector } from "@reduxjs/toolkit";

export type JobStatus = "PENDING" | "COMPLETED" | "IN_PROGRESS" | "ERROR";

export type JobType = "SUPPLY_CHAIN_LOT_EXPORT";

export interface Job {
  id: string;
  type: JobType;
  status: JobStatus;
  url?: string;
  error?: string;
  createdAt: string; // ISO string format
  fileName?: string;
}

interface AsyncJobsState {
  jobsById: Record<string, Job>;
  menuState: "expanded" | "collapsed" | "closed";
}

// Define which fields can be updated
export interface UpdateableJobFields {
  status?: JobStatus;
  url?: string;
  error?: string;
  fileName?: string;
}

// Type for the update action payload - requires both an ID and updatable fields
export type JobUpdate = { id: string } & UpdateableJobFields;

const initialState: AsyncJobsState = {
  jobsById: {},
  menuState: "closed",
};

export const asyncJobsSlice = createSlice({
  name: "asyncJobs",
  initialState,
  reducers: {
    setMenuState: (state, action: PayloadAction<AsyncJobsState["menuState"]>) => {
      state.menuState = action.payload;
    },
    addJob: (state, action: PayloadAction<Job>) => {
      state.jobsById[action.payload.id] = action.payload;
      // When adding a new job, ensure the menu is expanded
      if (state.menuState === "closed") {
        state.menuState = "expanded";
      }
    },
    updateJob: (state, action: PayloadAction<JobUpdate>) => {
      const { id, ...updates } = action.payload;
      if (state.jobsById[id]) {
        state.jobsById[id] = { ...state.jobsById[id], ...updates };
      }
    },
    removeJob: (state, action: PayloadAction<string>) => {
      const { [action.payload]: removedJob, ...remainingJobs } = state.jobsById;
      state.jobsById = remainingJobs;
    },
    clearAllJobs: (state) => {
      state.jobsById = {};
    },
  },
});

// Export actions
export const { setMenuState, addJob, updateJob, removeJob, clearAllJobs } = asyncJobsSlice.actions;

// Base selector
const selectAsyncJobsState = (state: { asyncJobs: AsyncJobsState }) => state.asyncJobs;

// Memoized selector for arrays/objects that create new references
export const selectJobs = createSelector([selectAsyncJobsState], (asyncJobs) => Object.values(asyncJobs.jobsById));

// Selector for active jobs only
export const selectActiveJobs = createSelector([selectJobs], (jobs) =>
  jobs.filter((job) => job.status !== "COMPLETED" && job.status !== "ERROR"),
);

// Check if there are any active (non-completed, non-error) jobs
export const selectHasActiveJobs = createSelector([selectActiveJobs], (activeJobs) => activeJobs.length > 0);

// Simple selectors for primitive values and direct object references
export const selectJobById = (state: { asyncJobs: AsyncJobsState }, id: string) => state.asyncJobs.jobsById[id];

export const selectMenuState = (state: { asyncJobs: AsyncJobsState }) => state.asyncJobs.menuState;

// Check if there are any jobs at all
export const selectHasAnyJobs = (state: { asyncJobs: AsyncJobsState }) =>
  Object.keys(state.asyncJobs.jobsById).length > 0;

export default asyncJobsSlice.reducer;
